import React, { useLayoutEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { PICKER_COLOR } from '@/components/common/datePicker/pickerTokens';
import { AmPm, formatTimeKo, slotsOf, splitAmPm } from '@/components/common/datePicker/pickerUtils';

/** 시안 altmock a2 — 오전/오후 세그먼트 + 30분 단위 큰 버튼 격자 */

const SLOT_OFF_COLOR: string = '#C7CFCC';
const SEGMENT_BG: string = '#F1F5F4';

const Segment = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  padding: 4px;
  margin-bottom: 10px;
  border-radius: 12px;
  background: ${SEGMENT_BG};
`;

const SegmentButton = styled.button<{ picked: boolean; compact: boolean }>`
  height: ${({ compact }) => (compact ? 32 : 40)}px;
  border: none;
  border-radius: 9px;
  background: ${({ picked }) => (picked ? '#fff' : 'transparent')};
  box-shadow: ${({ picked }) => (picked ? '0 1px 3px rgba(20, 30, 28, 0.12)' : 'none')};
  color: ${({ picked }) => (picked ? PICKER_COLOR.text : PICKER_COLOR.muted)};
  font-family: inherit;
  font-size: ${({ compact }) => (compact ? 14 : 16)}px;
  font-weight: ${({ picked }) => (picked ? 800 : 600)};
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

const Grid = styled.div<{ columns: number; compact: boolean }>`
  display: grid;
  grid-template-columns: repeat(${({ columns }) => columns}, minmax(0, 1fr));
  gap: ${({ compact }) => (compact ? 6 : 8)}px;
`;

const SlotButton = styled.button<{ picked: boolean; compact: boolean }>`
  height: ${({ compact }) => (compact ? 38 : 48)}px;
  border: 1px solid ${({ picked }) => (picked ? PICKER_COLOR.primary : PICKER_COLOR.border)};
  border-radius: 12px;
  background: ${({ picked }) => (picked ? PICKER_COLOR.primary : '#fff')};
  color: ${({ picked }) => (picked ? '#fff' : PICKER_COLOR.text)};
  font-family: inherit;
  font-size: ${({ compact }) => (compact ? 14 : 17)}px;
  font-weight: ${({ picked }) => (picked ? 800 : 500)};
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &:disabled {
    color: ${SLOT_OFF_COLOR};
    background: #fafbfb;
    border-color: ${PICKER_COLOR.line};
    cursor: default;
  }

  @media (hover: hover) {
    &:not(:disabled):hover {
      border-color: ${PICKER_COLOR.primary};
    }
  }

  &:focus-visible {
    outline: 2px solid ${PICKER_COLOR.primary};
    outline-offset: 1px;
  }
`;

export interface TimeGridProps {
  /** 보이는 칸(오전/오후) */
  ampm: AmPm;
  onAmpmChange: (ampm: AmPm) => void;
  /** 고른 시각 `HH:mm` */
  selected: string | null;
  isDisabled: (time: string) => boolean;
  onPick: (time: string) => void;
  /** 스크린리더 이름 앞말(예: "시작 일시") */
  label: string;
  /** 폰 3열 48px, PC 2열 38px */
  compact?: boolean;
  /** 날짜를 아직 안 골라 통째로 못 누름(PC) */
  inactive?: boolean;
}

/**
 * 시간 버튼 — 오전 칸 오전 7:00~11:30, 오후 칸 오후 12:00~10:00(30분 단위). 고를 수 없는 시각은 흐리게.
 * 열 때 고른 시각이 보이게 칸 안에서만 넘긴다.
 */
export const TimeGrid: React.FC<TimeGridProps> = ({ ampm, onAmpmChange, selected, isDisabled, onPick, label, compact = false, inactive = false }) => {
  const gridRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const picked: HTMLElement | null = gridRef.current?.querySelector<HTMLElement>('[aria-pressed="true"]') ?? null;
    picked?.scrollIntoView({ block: 'nearest' });
    // 열 때(와 오전/오후를 바꿀 때)만
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ampm]);

  return (
    <div>
      <Segment role="radiogroup" aria-label={`${label} 오전 오후`}>
        {(['AM', 'PM'] as AmPm[]).map((value) => (
          <SegmentButton
            key={value}
            type="button"
            role="radio"
            aria-checked={ampm === value}
            picked={ampm === value}
            compact={compact}
            disabled={inactive}
            onClick={() => onAmpmChange(value)}
          >
            {value === 'AM' ? '오전' : '오후'}
          </SegmentButton>
        ))}
      </Segment>
      <Grid ref={gridRef} columns={compact ? 2 : 3} compact={compact} role="group" aria-label={`${label} 시간`}>
        {slotsOf(ampm).map((time) => {
          const picked: boolean = selected === time;
          return (
            <SlotButton
              key={time}
              type="button"
              data-time={time}
              picked={picked}
              compact={compact}
              disabled={inactive || isDisabled(time)}
              aria-pressed={picked}
              aria-label={formatTimeKo(time)}
              onClick={() => onPick(time)}
            >
              {splitAmPm(time).clock}
            </SlotButton>
          );
        })}
      </Grid>
    </div>
  );
};

const HintRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
  font-size: 13px;
  color: ${PICKER_COLOR.muted};

  /* 작은 창 안 좁은 PC 팝업(FilterPanel data-popover-narrow) — 휠일 땐 안내 글 없이 [시간 버튼으로]만(폭 140 에서 두 줄로 꺾였다) */
  [data-popover-narrow] & {
    margin-top: 4px;
  }

  [data-popover-narrow] &[data-wheel] {
    justify-content: flex-end;
  }

  [data-popover-narrow] &[data-wheel] > span {
    display: none;
  }
`;

const LinkButton = styled.button`
  flex: 0 0 auto;
  min-height: 36px;
  padding: 0 2px;
  border: none;
  background: none;
  color: ${PICKER_COLOR.primaryDeep};
  font-family: inherit;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;

  [data-popover-narrow] & {
    min-height: 32px;
  }
`;

export interface TimeHintProps {
  /** 휠(직접 입력) 상태인지 */
  wheel: boolean;
  onToggle: () => void;
  /** PC 좁은 칸 — 짧은 문구 */
  compact?: boolean;
}

/** "30분 단위" + [다른 시간 직접 입력] / "분은 10분 단위" + [시간 버튼으로] */
export const TimeHint: React.FC<TimeHintProps> = ({ wheel, onToggle, compact = false }) => (
  <HintRow data-wheel={wheel ? '' : undefined}>
    <span>{wheel ? '분은 10분 단위' : '30분 단위'}</span>
    <LinkButton type="button" onClick={onToggle}>
      {wheel ? '시간 버튼으로' : compact ? '직접 입력' : '다른 시간 직접 입력'}
    </LinkButton>
  </HintRow>
);
